import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import designTokens from './theme/designTokens';

// Pages
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ChatRoomList from './pages/ChatRoomList';
import ChatRoom from './pages/ChatRoom';
import CreateChatRoom from './pages/CreateChatRoom';
import EndUserLogin from './pages/EndUserLogin';
import EndUserChat from './pages/EndUserChat';
import EmailOTPVerification from './pages/EmailOTPVerification';
import EmailTest from './pages/EmailTest';
import AccountManagement from './pages/AccountManagement';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import NewChatInterface from './pages/NewChatInterface';
import CustomerChat from './pages/CustomerChat';
import TestPage from './pages/TestPage';
import AdminLoginV2 from './pages/AdminLoginV2';
import HelpManual from './pages/HelpManual';
import ReleaseNotes from './pages/ReleaseNotes';
import ResponseTimeReport from './pages/ResponseTimeReport';
import TemplateManagement from './pages/TemplateManagement';
import RoomManagement from './pages/RoomManagement';
import FileManagement from './pages/FileManagement';
import AuditLogs from './pages/AuditLogs';
import ExternalApiKeys from './pages/ExternalApiKeys';

// Components
import { RoleBasedRoute } from './components/RoleBasedRoute';
import { PERMISSIONS } from './types';

/**
 * 認証済みユーザーのみ子コンポーネントをレンダリングする軽量ガード。
 * NewChatInterface のような重いコンポーネントがマウントされる前に
 * 未認証ユーザーを /login へリダイレクトし、白画面を防ぐ。
 */
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = (() => {
    try {
      return !!localStorage.getItem('adminUser');
    } catch {
      return false;
    }
  })();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// テーマ設定
const theme = createTheme({
  shadows: Array(25).fill('none') as any, // 影を全面無効化
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
    // 全体の仕切り線カラーを薄いグレーに統一（黒っぽい線を回避）
    divider: '#E5E7EB', // Tailwind gray-200 相当
  },
  typography: {
    fontFamily: [
      'Helvetica Neue',
      'Arial',
      'Hiragino Kaku Gothic ProN',
      'Hiragino Sans',
      'Meiryo',
      'sans-serif',
    ].join(','),
    // UI Foundations Kit風のタイポグラフィ設定
    fontSize: 13, // ベースフォントサイズを14pxから13pxに縮小
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '0.95rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem', // 14px → 13px相当
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem', // 13px → 12px相当
      fontWeight: 400,
      lineHeight: 1.4,
    },
    caption: {
      fontSize: '0.75rem', // 12px → 11px相当
      fontWeight: 400,
      lineHeight: 1.3,
    },
    overline: {
      fontSize: '0.6875rem', // 11px → 10px相当
      fontWeight: 500,
      lineHeight: 1.2,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
    button: {
      fontSize: '0.8125rem', // ボタンテキストもコンパクトに
      fontWeight: 500,
      lineHeight: 1.4,
      textTransform: 'none',
    },
  },
  components: {
    // シャドウ最小化ポリシーに基づくコンポーネントスタイル
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: designTokens.elevation.none,
          border: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          padding: '6px 16px',
          minHeight: 32,
        },
        sizeSmall: {
          fontSize: '0.75rem',
          padding: '4px 12px',
          minHeight: 28,
        },
        sizeLarge: {
          fontSize: '0.875rem',
          padding: '8px 20px',
          minHeight: 36,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
          border: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          paddingTop: 8,
          paddingBottom: 8,
          // テーブルの境界線を削除
          border: 'none',
        },
        head: {
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.7)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'grey.200',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.8125rem',
          fontWeight: 500,
        },
        secondary: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          height: 24,
        },
        sizeSmall: {
          fontSize: '0.6875rem',
          height: 20,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          minHeight: 36,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontSize: '0.8125rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.8125rem',
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ height: '100vh', backgroundColor: 'background.default', overflowX: 'hidden', overflowY: 'auto' }}>
        <Router>
          <Routes>
            <Route path="/login" element={<AdminLoginV2 />} />
            <Route path="/email/verify" element={<EmailOTPVerification />} />
            <Route path="/" element={<AuthGuard><NewChatInterface /></AuthGuard>} />
            <Route path="/admin" element={<AuthGuard><NewChatInterface /></AuthGuard>} />
            <Route
              path="/admin/settings"
              element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <Settings />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <RoleBasedRoute allowedRoles={['admin', 'operator']}>
                  <Reports />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/accounts"
              element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <AccountManagement />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/users/new"
              element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <AccountManagement />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/help"
              element={
                <RoleBasedRoute allowedRoles={['admin', 'operator']}>
                  <HelpManual />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/release-notes"
              element={
                <RoleBasedRoute allowedRoles={['admin', 'operator']}>
                  <ReleaseNotes />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/response-time"
              element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <ResponseTimeReport />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/templates"
              element={
                <RoleBasedRoute allowedRoles={['admin', 'operator']}>
                  <TemplateManagement />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/rooms"
              element={
                <RoleBasedRoute allowedRoles={['admin', 'operator']}>
                  <RoomManagement />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/files"
              element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <FileManagement />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <AuditLogs />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/admin/external-api"
              element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <ExternalApiKeys />
                </RoleBasedRoute>
              }
            />
            <Route path="/chat/:roomId" element={<CustomerChat />} />
            <Route path="/test" element={<TestPage />} />
            
            {/* エンドユーザー用ルート */}
            <Route path="/user/login" element={<EndUserLogin />} />
            <Route path="/user/verify" element={<EmailOTPVerification />} />
            <Route path="/user/chat/:roomId" element={<EndUserChat />} />
          </Routes>
        </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;
